// @flow
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { SketchOrderStatus, SketchOrderStatusId } from '../../lib/sketchOrderStatuses';
import QueryStringBuilder from '../../lib/queryStringBuilder';
import LocalStorageWrapper from '../../lib/localStorageWrapper';
import { getAllSketchOrderCounts, updateAllSketchOrderCounts } from '../../actions/sketchOrderDashboardActions';
import {
    JsonHubProtocol,
    HubConnectionState,
    HubConnectionBuilder,
    LogLevel
} from '@microsoft/signalr';
import { UPDATE_ALL_SKETCH_ORDER_COUNTS } from '../../actions/actionTypes';

type ProvidedProps = {
    history: {
        push: Function,
    },
    dispatch: Function,
    totalOrdersInTheQueue: number,
    totalOrdersPendingSketchOsAdmin: number,
    totalExceptionOrders: number,
    totalMyOrdersInProgress: number,
    pendingMetalRoofOrders: number,
    currentUser: {
        userId: number,
    },
    isSketchOsAdmin: boolean,
};

const pendingStatusFilters = [
    {
        id: SketchOrderStatusId.Pending,
        status: SketchOrderStatus.Pending,
    },
    {
        id: SketchOrderStatusId.InProgress,
        status: SketchOrderStatus.InProgress,
    },
    {
        id: SketchOrderStatusId.PendingReview,
        status: SketchOrderStatus.PendingReview,
    },
    {
        id: SketchOrderStatusId.Exception,
        status: SketchOrderStatus.Exception,
    },
];

const withOrdersRouter = (WrappedComponent: typeof React.Component) => {
    class HOC extends Component<ProvidedProps> {

        async componentDidMount() {
            await this.props.dispatch(getAllSketchOrderCounts());
            // TODO: Re-enable once we've fixed identity issues
            // this.subscribeSignalR();
        }
        
        goToPendingOrders = () => {
            this.props.history.push('/queue');
        };

        goToPendingVendorOrders = () => {
            this.props.history.push('/queue/vendor');
        };

        goToMyOrdersInProgress = () => {
            const queryStringBuilder = new QueryStringBuilder();
            queryStringBuilder.setQueryParameter('statusIds', SketchOrderStatusId.InProgress);
            const queryParams = queryStringBuilder.buildQueryString();
            this.props.history.push(`/myorders${queryParams}`);
        };

        goToOrdersWithFilter = async (statusFilters) => {
            const queryStringBuilder = new QueryStringBuilder();
            statusFilters.forEach((status) => {
                queryStringBuilder.setQueryParameter('statusIds', status.id);
            });
            const queryParams = queryStringBuilder.buildQueryString();
            this.props.history.push(`/orders${queryParams}`);
        };

        goToOrdersPendingSketchOsAdmin = async () => {
            const statusFilters = [
                {
                    id: SketchOrderStatusId.PendingReview,
                    status: SketchOrderStatus.PendingReview,
                },
                {
                    id: SketchOrderStatusId.PendingRejection,
                    status: SketchOrderStatus.PendingRejection,
                }
                ,
                {
                    id: SketchOrderStatusId.InReview,
                    status: SketchOrderStatus.InReview,
                }
            ];
            await this.goToOrdersWithFilter(statusFilters);
        };

        goToOrdersWithException = async () => {
            const statusFilters = [
                {
                    id: SketchOrderStatusId.Exception,
                    status: SketchOrderStatus.Exception,
                },
            ];
            await this.goToOrdersWithFilter(statusFilters);
        };

        goToBillingFailedOrders = async () => {
            const statusFilters = [
                {
                    id: SketchOrderStatusId.BillingFailed,
                    status: SketchOrderStatus.BillingFailed,
                },
            ];
            await this.goToOrdersWithFilter(statusFilters);
        };

        goToRushOrders= async () => {
            const queryStringBuilder = new QueryStringBuilder();
            queryStringBuilder.setQueryParameter('rush', 'true');
            
            pendingStatusFilters.forEach((status) => {
                queryStringBuilder.setQueryParameter('statusIds', status.id);
            });

            const queryParams = queryStringBuilder.buildQueryString();
            this.props.history.push(`/orders${queryParams}`);
        };

        goToMetalRoofOrders = () => {
            const queryStringBuilder = new QueryStringBuilder();
            queryStringBuilder.setQueryParameter('metalRoof', 'true');

            pendingStatusFilters.forEach((status) => {
                queryStringBuilder.setQueryParameter('statusIds', status.id);
            });

            const queryParams = queryStringBuilder.buildQueryString();
            this.props.history.push(`/orders${queryParams}`);
        }

        subscribeSignalR = () => {

            const loginToken = LocalStorageWrapper.getItem('accessToken')
            var connection = new HubConnectionBuilder().withUrl(`${process.env.REACT_APP_ORDERS_HUB_URL}`, { accessTokenFactory: () => loginToken }).build(); //Disable the send button until connection is established.
            connection.on("ReceiveOrderCounts", (orders) => {
                this.props.dispatch(updateAllSketchOrderCounts(orders))
            });
    
            connection.start().then(() => {
                connection.invoke("ReceiveOrderCounts").catch((err) => {
                    return console.error(err.toString());
                });
            })
        }

        render() {
            return (
                <WrappedComponent
                    goToPendingOrders={this.goToPendingOrders}
                    goToPendingVendorOrders={this.goToPendingVendorOrders}
                    goToMyOrdersInProgress={this.goToMyOrdersInProgress}
                    goToOrdersPendingSketchOsAdmin={this.goToOrdersPendingSketchOsAdmin}
                    goToOrdersWithException={this.goToOrdersWithException}
                    goToBillingFailedOrders={this.goToBillingFailedOrders}
                    goToRushOrders={this.goToRushOrders}
                    goToMetalRoofOrders={this.goToMetalRoofOrders}
                    isSketchOsAdmin={this.props.isSketchOsAdmin}
                    {...this.props}
                />
            );
        }
    }
    const mapStateToProps = (state) =>{
        const { dashboard, currentUserRoles, currentUser } = state;
        const {
            isFetchingCounts,
            totalOrdersInTheQueue,
            totalOrdersInTheVendorQueue,            
            totalOrdersPendingSketchOsAdmin,            
            totalExceptionOrders,
            totalMyOrdersInProgress,
            totalBillingFailedOrders,
            totalRushOrders,
            filteredRushOrders,
            pendingMetalRoofOrders,
        } = dashboard;
        return {
            isFetchingCounts,
            totalOrdersInTheQueue,
            totalOrdersInTheVendorQueue,
            totalOrdersPendingSketchOsAdmin,
            totalExceptionOrders,
            totalMyOrdersInProgress,
            totalBillingFailedOrders,
            totalRushOrders,
            filteredRushOrders,
            pendingMetalRoofOrders,
            currentUser,
            isSketchOsAdmin: !!(currentUserRoles.isAdmin || currentUserRoles.isSketchOsAdmin),
        };
    };
    return connect(mapStateToProps)(withRouter(HOC));
};

export default withOrdersRouter;
